import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { onAuthFailure, resetAuthFailure } from '../apollo';
import { sessionExpired } from '../state/authSlice';
import { setAssignedRole } from '../state/uiSlice';

/**
 * Ends the session the moment the server stops accepting it.
 *
 * Without this the app sat on a dead token: every screen rendered its error
 * state, the sidebar still showed a signed-in desk, and nothing said the
 * session had gone. The only way out was to know to reload.
 *
 * Renders nothing, and deliberately does NOT navigate. `ProtectedRoute`
 * already sends an unauthenticated session to the sign-in form, so clearing
 * the session is enough — and the router's own comment records why a second
 * navigation is a bad idea here: two updates racing meant sign-out sometimes
 * landed on the wrong page. Clear the state, let the guard do the routing.
 *
 * Mounted at the root rather than inside the app shell, because a token can
 * die on any screen, including the marketing and auth pages that render
 * outside the shell.
 */
export default function SessionWatch() {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const isLoggedIn = useSelector((s) => s.auth.isLoggedIn);

  useEffect(() => onAuthFailure(() => {
    dispatch(sessionExpired());
    /* The seat came from the token, so it goes with it — otherwise the next
       person to sign in on this browser briefly gets the last one's screens. */
    dispatch(setAssignedRole(null));
    /* Drop everything read as the old user. resetStore would refetch active
       queries against a session that no longer exists, so clearStore. */
    client.clearStore().catch(() => {});
  }), [dispatch, client]);

  /* Signing in again re-arms the watch, which mutes itself after firing so a
     hundred simultaneous failures end the session once. */
  useEffect(() => {
    if (isLoggedIn) resetAuthFailure();
  }, [isLoggedIn]);

  return null;
}
